/**
 * The hooks views use: the services from context, the live session and
 * theme, and the data hooks — each a one-line binding of a `…Query` /
 * `…Mutation` factory (`src/api/`) to this app's backend.
 *
 * Only views (`src/views/`) call these. Presentational components never do:
 * `stories.test.ts` fails a component that imports from `app/`, `api/`
 * (values), `auth/` or TanStack Query.
 */
import type { ResourceKind } from '@acp/api-contract/changes.js';
import { createUlidFactory } from '@acp/domain/ulid.js';
import { type QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { type ChangeFeedDeps, createChangeFeedSession } from '../api/changes.js';
import type { HeardChange } from '../lib/live/changeLog.js';
import { absorb, initialLive, type LiveActions, liveActions, type LiveNotice, type LiveState } from '../lib/live/liveResource.js';
import { cloudProductionsQuery, launchCloudProductionMutation, stopCloudProductionMutation } from '../api/cloudProductions.js';
import { meQuery } from '../api/identity.js';
import { localAuthQuery } from '../api/localAuth.js';
import { createRundownEditorSession, type EditorSession, type EditorSessionDeps, type EditorSnapshot } from '../api/rundownEditor.js';
import { createPlayoutSession, type PlayoutNotice, type PlayoutSession, type PlayoutSessionDeps, type PlayoutSnapshot } from '../api/playout.js';
import { asRunQuery, createProductionMutation, endProductionMutation, playlistsQuery, productionQuery, productionsQuery, switchoverMutation } from '../api/productions.js';
import { createRundownMutation, rundownsQuery } from '../api/rundowns.js';
import {
  createShowStyleMutation,
  deleteShowStyleMutation,
  saveShowStyleMutation,
  showStyleQuery,
  showStylesQuery,
  validateShowStyleQuery,
} from '../api/showStyles.js';
import { createStudioMutation, saveStudioMutation, studioQuery, studiosQuery } from '../api/studios.js';
import { type Grants, grantsOf } from '../lib/permissions.js';
import type { Session } from '../auth/session.js';
import type { ConnectivityState } from '../connectivity/state.js';
import type { Theme } from '../lib/theme.js';
import { browserRandomBytes, browserSocket, browserTimers } from './browserPorts.js';
import type { Services } from './services.js';

export const ServicesContext = createContext<Services | null>(null);

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (services === null) throw new Error('useServices() outside <ServicesContext.Provider> — views render inside startApp()');
  return services;
}

export function useSession(): Session | null {
  const { sessions } = useServices();
  return useSyncExternalStore(sessions.subscribe, sessions.getSession, sessions.getSession);
}

export function useTheme(): Theme {
  const { theme } = useServices();
  return useSyncExternalStore(theme.subscribe, theme.get, theme.get);
}

/** The live connectivity state (PLAN §10.2): every watched endpoint, the primary, the socket. */
export function useConnectivity(): ConnectivityState {
  const { connectivity } = useServices();
  return useSyncExternalStore(connectivity.subscribe, connectivity.getState, connectivity.getState);
}

/** The primary backend's endpoint id: changes only on a failover or the operator's switch (not on every probe). */
export function usePrimaryId(): string {
  const { connectivity } = useServices();
  const primaryId = () => connectivity.getState().primaryId;
  return useSyncExternalStore(connectivity.subscribe, primaryId, primaryId);
}

/** The node's sign-in status (`GET /local/auth`, D20), polled; idle when the page is not served by a node. */
export function useLocalAuth() {
  return useQuery(localAuthQuery(useServices().offline?.status ?? null));
}

export function useRundowns() {
  return useQuery(rundownsQuery(useServices().backend.api));
}

export function useCreateRundown() {
  const queryClient = useQueryClient();
  return useMutation(createRundownMutation(useServices().backend.api, queryClient));
}

export function useMe() {
  return useQuery(meQuery(useServices().backend.api));
}

/**
 * The live editor session for one rundown (`api/rundownEditor.ts`), created
 * once per mount and started/stopped with it. Remount (a `key`) for another
 * rundown.
 */
export function useRundownEditorSession(rundownId: string, notify: EditorSessionDeps['notify']): EditorSession {
  const services = useServices();
  const { backend, sessions, connectivity } = services;
  const tenantId = useSession()?.tenantId ?? '';
  const primaryId = usePrimaryId();
  const [session] = useState(() =>
    createRundownEditorSession({
      api: backend.api,
      // Read at every subscribe: the getter names the CURRENT primary.
      wsUrl: () => backend.wsUrl,
      reportSocket: connectivity.reportSocket,
      tenantId,
      rundownId,
      token: () => (backend.kind === 'node' ? sessions.nodeToken(false) : sessions.idToken(false)),
      openSocket: browserSocket,
      timers: browserTimers,
      nowMs: services.nowMs,
      parsing: services.responses,
      randomBytes: browserRandomBytes,
      notify,
    }),
  );
  useEffect(() => {
    session.start();
    return () => session.stop();
  }, [session]);
  // A failover or the operator's switch: reload from the new primary and resubscribe there.
  useEffect(() => {
    session.setPrimary(primaryId);
  }, [session, primaryId]);
  return session;
}

/** The session's snapshot, re-rendering on every change. */
export function useEditorSnapshot(session: EditorSession): EditorSnapshot {
  return useSyncExternalStore(session.subscribe, session.snapshot, session.snapshot);
}

/** The signed-in user's grants (D14) for hiding and disabling controls; null (unknown) on a node, which serves no `/me`. */
export function useGrants(): Grants {
  return grantsOf(useMe().data);
}

/** The browser's clock, re-read every `intervalMs` (the playout view's countdowns tick on it). */
export function useNow(intervalMs: number): number {
  const { nowMs } = useServices();
  const [now, setNow] = useState(nowMs);
  useEffect(() => browserTimers.every(intervalMs, () => setNow(nowMs())), [intervalMs, nowMs]);
  return now;
}

/** A ULID factory on the browser's clock and randomness (ids the client mints: productions, commands). */
export function useIdFactory(): () => string {
  const { nowMs } = useServices();
  const [newId] = useState(() => createUlidFactory(nowMs, browserRandomBytes));
  return newId;
}

// ─── studios ───

export function useStudios() {
  return useQuery(studiosQuery(useServices().backend.api));
}

export function useStudio(studioId: string) {
  return useQuery(studioQuery(useServices().backend.api, studioId));
}

export function useCreateStudio() {
  const queryClient = useQueryClient();
  return useMutation(createStudioMutation(useServices().backend.api, queryClient));
}

export function useSaveStudio() {
  const queryClient = useQueryClient();
  return useMutation(saveStudioMutation(useServices().backend.api, queryClient));
}

// ─── show styles (D32) ───

export function useShowStyles() {
  return useQuery(showStylesQuery(useServices().backend.api));
}

/** One show style; `null` fetches nothing (a studio with no show style chosen). */
export function useShowStyle(showStyleId: string | null) {
  return useQuery({ ...showStyleQuery(useServices().backend.api, showStyleId ?? ''), enabled: showStyleId !== null });
}

/** The server's judgement of a pair; `null` for either fetches nothing. */
export function useValidateShowStyle(showStyleId: string | null, studioId: string | null) {
  return useQuery({ ...validateShowStyleQuery(useServices().backend.api, showStyleId ?? '', studioId ?? ''), enabled: showStyleId !== null && studioId !== null });
}

export function useCreateShowStyle() {
  const queryClient = useQueryClient();
  return useMutation(createShowStyleMutation(useServices().backend.api, queryClient));
}

export function useSaveShowStyle() {
  const queryClient = useQueryClient();
  return useMutation(saveShowStyleMutation(useServices().backend.api, queryClient));
}

export function useDeleteShowStyle() {
  const queryClient = useQueryClient();
  return useMutation(deleteShowStyleMutation(useServices().backend.api, queryClient));
}

// ─── productions ───

export function useProductions() {
  return useQuery(productionsQuery(useServices().backend.api));
}

export function useProduction(productionId: string) {
  return useQuery(productionQuery(useServices().backend.api, productionId));
}

export function usePlaylists() {
  return useQuery(playlistsQuery(useServices().backend.api));
}

export function useCreateProduction() {
  const queryClient = useQueryClient();
  const newId = useIdFactory();
  return useMutation(createProductionMutation(useServices().backend.api, queryClient, newId));
}

export function useEndProduction() {
  const queryClient = useQueryClient();
  return useMutation(endProductionMutation(useServices().backend.api, queryClient));
}

/** A manual switchover of a production to the other node of its pair (PLAN §8.4). */
export function useSwitchover() {
  return useMutation(switchoverMutation(useServices().backend.api));
}

// ─── cloud productions (PLAN §9) ───

/** The cloud productions and the network they run in; re-read every few seconds while one is on the move. */
export function useCloudProductions() {
  return useQuery(cloudProductionsQuery(useServices().backend.api));
}

export function useLaunchCloudProduction() {
  const queryClient = useQueryClient();
  const newId = useIdFactory();
  return useMutation(launchCloudProductionMutation(useServices().backend.api, queryClient, newId));
}

export function useStopCloudProduction() {
  const queryClient = useQueryClient();
  return useMutation(stopCloudProductionMutation(useServices().backend.api, queryClient));
}

export function useAsRun(productionId: string) {
  return useInfiniteQuery(asRunQuery(useServices().backend.api, productionId));
}

/**
 * The live playout session for one production (`api/playout.ts`), created
 * once per mount and started/stopped with it. Remount (a `key`) for another
 * production.
 */
/** The ports a playout session takes, from the app's services (the browser's socket and timers). */
export function playoutSessionDeps(services: Services, tenantId: string, productionId: string, newId: () => string, notify: (notice: PlayoutNotice) => void): PlayoutSessionDeps {
  const { backend, sessions, connectivity } = services;
  return {
    api: backend.api,
    // Read at every subscribe: the getter names the CURRENT primary.
    wsUrl: () => backend.wsUrl,
    reportSocket: connectivity.reportSocket,
    tenantId,
    productionId,
    token: () => (backend.kind === 'node' ? sessions.nodeToken(false) : sessions.idToken(false)),
    openSocket: browserSocket,
    timers: browserTimers,
    nowMs: services.nowMs,
    parsing: services.responses,
    newCommandId: () => `web-${newId()}`,
    notify,
  };
}

/**
 * The live playout session for one production (`api/playout.ts`), created
 * once per mount and started/stopped with it. Remount (a `key`) for another
 * production.
 */
export function usePlayoutSession(productionId: string, notify: (notice: PlayoutNotice) => void): PlayoutSession {
  const services = useServices();
  const tenantId = useSession()?.tenantId ?? '';
  const primaryId = usePrimaryId();
  const newId = useIdFactory();
  const [session] = useState(() => createPlayoutSession(playoutSessionDeps(services, tenantId, productionId, newId, notify)));
  useEffect(() => {
    session.start();
    return () => session.stop();
  }, [session]);
  useEffect(() => {
    session.setPrimary(primaryId);
  }, [session, primaryId]);
  return session;
}

export function usePlayoutSnapshot(session: PlayoutSession): PlayoutSnapshot {
  return useSyncExternalStore(session.subscribe, session.snapshot, session.snapshot);
}

// ─── live updates (D35) ───

/** The ports the page's change feed takes, from the app's services (the browser's socket and timers). */
export function changeFeedDeps(services: Services, tenantId: string, queryClient: Pick<QueryClient, 'invalidateQueries'>): ChangeFeedDeps {
  const { backend, sessions, connectivity } = services;
  return {
    // Read at every connect: the getter names the CURRENT primary.
    wsUrl: () => backend.wsUrl,
    tenantId,
    token: () => (backend.kind === 'node' ? sessions.nodeToken(false) : sessions.idToken(false)),
    openSocket: browserSocket,
    timers: browserTimers,
    nowMs: services.nowMs,
    parsing: services.responses,
    reportSocket: connectivity.reportSocket,
    invalidate: (queryKey) => queryClient.invalidateQueries({ queryKey }),
    log: services.liveChanges,
  };
}

/**
 * The page's tenant change channel: one socket while signed in (`AppLayout`
 * mounts it, on a cloud page and a node's alike), each `resource.changed`
 * invalidating the matching queries (`api/changes.ts`: every list and detail
 * refetches on its own) and landing in the change log for open editors.
 */
export function useLiveChanges(): void {
  const services = useServices();
  const queryClient = useQueryClient();
  const tenantId = useSession()?.tenantId ?? '';
  const primaryId = usePrimaryId();
  const [session] = useState(() => createChangeFeedSession(changeFeedDeps(services, tenantId, queryClient)));
  useEffect(() => {
    session.start();
    return () => session.stop();
  }, [session]);
  useEffect(() => {
    session.setPrimary(primaryId);
  }, [session, primaryId]);
}

export type LiveResourceOptions<T> = {
  readonly kind: ResourceKind;
  readonly id: string;
  /** The version the page opened with. */
  readonly loaded: T;
  /** The query's data now: a refetch after a change (or our own save's result) lands here. */
  readonly latest: T | undefined;
  /** Whether the draft differs from `base` (the page's own judgement), asked about the hook's current base. */
  readonly isDirty: (base: T) => boolean;
  readonly versionOf: (value: T) => number;
  /** Who wrote a version, when the change channel has not said (its `updatedBy`). */
  readonly authorOf: (value: T) => string;
  /** When a version was written (ms), when the change channel has not said. */
  readonly writtenAtOf: (value: T) => number;
  /** Replace the draft with this version (refreshed in place, theirs loaded, or ours saved). */
  readonly onReset: (value: T) => void;
};

/**
 * An editor's live state and actions: `beginSave` before a save (what the query shows meanwhile is ours in
 * flight), `saved` / `conflict` (its 409: the server's version, the same two choices as a change heard) /
 * `saveFailed` after; `keepMine` ("Overwrite with mine": re-base on theirs, returns the version to save against)
 * and `discardMine` ("Discard mine and load theirs").
 */
export type LiveResource<T> = LiveActions<T> & {
  /** The version the draft is made from: what a save names. */
  readonly base: T;
  readonly notice: LiveNotice<T> | null;
  /** How many times the page was refreshed in place: a keyed overlay flashes on each (`animate-live-flash`). */
  readonly refreshes: number;
};

/**
 * The dirty-aware editor (D35, `lib/live/liveResource.ts`) bound to the
 * page: the query's data and the change log in, the base / notice /
 * highlight out, and the draft reset through `onReset`. Any editor of a
 * versioned resource uses it the same way — the studio and show-style pages
 * now, the admin screens next.
 */
export function useLiveResource<T>(options: LiveResourceOptions<T>): LiveResource<T> {
  const { kind, id, latest, versionOf, onReset } = options;
  const { liveChanges } = useServices();
  const lastHeard = () => liveChanges.last(kind, id);
  const heard = useSyncExternalStore(liveChanges.subscribe, lastHeard, lastHeard);
  const [state, setState] = useState<LiveState<T>>(() => initialLive(options.loaded));
  const [seenLatest, setSeenLatest] = useState<T | undefined>(latest);
  const [seenHeard, setSeenHeard] = useState<HeardChange | null>(heard);
  const [saving, setSaving] = useState(false);

  const reader = { versionOf, authorOf: options.authorOf, writtenAtOf: options.writtenAtOf };

  // Adjusting state to new inputs during render (React's documented pattern; no effect, no extra frame).
  if (heard !== seenHeard || latest !== seenLatest) {
    const absorbed = absorb(state, { heard, seenHeard, latest, seenLatest, saving, isDirty: options.isDirty }, reader);
    setSeenHeard(heard);
    setSeenLatest(latest);
    if (absorbed.state !== state) setState(absorbed.state);
    if (absorbed.reset !== null) onReset(absorbed.reset);
  }

  return { base: state.base, notice: state.notice, refreshes: state.refreshes, ...liveActions({ state, setState, setSaving, onReset, heard, reader }) };
}
